import { useState, useLayoutEffect, useMemo, useRef } from 'react';
import { buildLayouts, getInitialLayout, computeDotRow } from '../layouts/index';

/**
 * Manages the active layout state and all layout definitions.
 *
 * Uses two-level memoization to avoid rebuilding ALL layouts on every keystroke:
 *   1. baseLayouts — depends on config props (not typed)
 *   2. dotRow      — depends only on isDot + typed
 *   3. allLayouts  — merges the two
 *
 * @param {object} opts
 */
export function useKeyboardState({
  type,
  typed,
  isDone,
  isClr,
  zipcodeFlag,
  isDot,
  isOrderSeat,
  isOfflinePay,
  callApi,
  orderSearch,
  screenType,
}) {
  // Level 1: everything except typed — stable during normal typing
  const baseLayouts = useMemo(
    () =>
      buildLayouts({
        isDone,
        isClr,
        zipcodeFlag,
        isDot,
        isOrderSeat,
        isOfflinePay,
        callApi,
        orderSearch,
        screenType,
        dotRow: computeDotRow(isDot, ''), // placeholder; overridden below
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isDone, isClr, zipcodeFlag, isDot, isOrderSeat, isOfflinePay, callApi, orderSearch, screenType],
  );

  // Level 2: only the row that varies with typed.
  // When isDot is false, computeDotRow always returns the same value regardless of typed,
  // so we gate the typed dependency to avoid rebuilding allLayouts on every keystroke
  // for keyboards that never use a dot row (numeric, swipe, text, etc.).
  const typedForDot = isDot ? typed : undefined;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dotRow = useMemo(() => computeDotRow(isDot, typed), [isDot, typedForDot]);

  // Level 3: merge — only invalidates when baseLayouts or dotRow changes
  const allLayouts = useMemo(
    () => ({
      ...baseLayouts,
      number: { ...baseLayouts.number, numericDefaultWithDot: dotRow },
    }),
    [baseLayouts, dotRow],
  );

  // Initial layout derived from type
  const [layout, setLayout] = useState(() => getInitialLayout(type, allLayouts));

  // Track previous type to detect actual changes
  const prevTypeRef = useRef(type);

  useLayoutEffect(() => {
    const typeChanged = prevTypeRef.current !== type;
    prevTypeRef.current = type;
    if (typeChanged || type === 'dot') {
      setLayout(getInitialLayout(type, allLayouts));
    }
  }, [type, allLayouts]);

  return { layout, setLayout, allLayouts };
}
